import { PropTypes } from "prop-types";
import { Button } from "../../Button";
import AddIcon from "../../../assets/img/icons/add.svg";
import { useState } from "react";
import Modal from "react-modal";
import { ConfirmationModal } from "../../ConfirmationModal";
import { StyledDirectionTableContainer, StyledDirectionTableHeader } from "../../../styles/StyledDecision/StyledContent";
import { DecisionDirection } from ".";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { getDirectionsList, setDirectionsList } from "../../../api/decision/directions";
import { useQuery } from "react-query";
import { QueryLoader } from "../../Loader";
import { useEffect } from "react";
import useDecisionAccess from "../../../hooks/useDecisionAccess";
import ModalHeader from "../../ModalHeader/ModalHeader";
import { AddDirection } from ".";
import { StyledModal, StyledPointsModal } from "../../../styles/StyledModal";

export default function DecisionDirections({ id, newDecision = false }) {
	const { canEdit } = useDecisionAccess(newDecision ? null : id);
	const editable = newDecision || canEdit;
	const axiosHandler = useAxiosPrivate();
	const [wasEdited, setWasEdited] = useState(false);
	const [tempDirections, setTempDirections] = useState([]);

	// For new decisions, don't make API calls - use temporary storage
	const shouldUseAPI = !newDecision && id;
	const { isLoading, isError, error, data, refetch } = shouldUseAPI
		? useQuery(["directionsTab", id, wasEdited], () => getDirectionsList(axiosHandler.axiosPrivate, id))
		: { isLoading: false, isError: false, error: null, data: tempDirections, refetch: () => { } };

	const [resultDirections, setResultDirections] = useState([]);
	const [editedDirections, setEditedDirections] = useState([]);
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [deletedId, setDeletedId] = useState(null);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [editedId, setEditedId] = useState(null);

	useEffect(() => {
		if (isError) console.log(error);

		const safeData = data || [];
		if (!isLoading) setResultDirections([...safeData]);
		if (!isLoading) setEditedDirections([...safeData]);
	}, [isLoading, data]);

	const handleDelete = async () => {
		if (newDecision) {
			// For new decisions, use tempId for filtering
			const res = tempDirections.filter((e) => (e.tempId || e.id) !== deletedId);
			setTempDirections(res);
			setEditedDirections(res);
			setResultDirections(res);
		} else {
			const res = editedDirections.filter((e) => parseInt(e.id) !== parseInt(deletedId));
			updateDirections(res);
		}
		setModalIsOpen(false);
	};

	const updateDirections = async (dirs = editedDirections) => {
		setIsEditModalOpen(false);

		if (newDecision) {
			// For new decisions, ensure all directions have temporary IDs
			const dirsWithIds = dirs.map((dir, index) => ({
				...dir,
				tempId: dir.tempId || dir.id || `temp_${Date.now()}_${index}`,
				id: dir.tempId || dir.id || `temp_${Date.now()}_${index}`
			}));

			setTempDirections(dirsWithIds);
			setEditedDirections(dirsWithIds);
			setResultDirections(dirsWithIds);
			return;
		}

		const res = [];

		dirs.map((e) => {
			if (!e.id) {
				res.push(e);
			} else {
				const tempObj = {};

				tempObj.description = e.description;
				if (e?.followDecision) {
					// Handle both ID strings/numbers and objects with ID
					tempObj.followDecision = typeof e.followDecision === "object" ? e.followDecision.id : e.followDecision;
				} else if (e?.responsibleUser) {
					// Handle both ID strings/numbers and objects with ID
					tempObj.responsibleUser = typeof e.responsibleUser === "object" ? e.responsibleUser.id : e.responsibleUser;
				} else if (e?.responsibleUnit) {
					// Handle both ID strings/numbers and objects with ID
					tempObj.responsibleUnit = typeof e.responsibleUnit === "object" ? e.responsibleUnit.id : e.responsibleUnit;
				} else if (e?.url) {
					tempObj.url = e.url;
					tempObj.urlLabel = e.urlLabel;
				}

				res.push(tempObj);
			}
		});

		console.log("Sending directions to API:", { directions: res });
		await setDirectionsList(axiosHandler.axiosPrivate, id, { directions: res }, () => setWasEdited(!wasEdited));
		await setTimeout(() => refetch(), 1000);
	};

	return (
		<>
			{isLoading ? (
				<QueryLoader />
			) : (
				<>
					<StyledDirectionTableHeader>
						<span>Order</span>
						<span>Description</span>
						<span>Type</span>
						<span>Details</span>
						{editable && <span>Actions</span>}
					</StyledDirectionTableHeader>
					<StyledDirectionTableContainer>
						{(data && data.length > 0) ? (
							data.map((e, i) => {
								// Ensure each direction has an ID for the component
								const directionData = {
									...e,
									id: e.id || e.tempId || `temp_${i}_${Date.now()}`
								};
								return (
									<DecisionDirection
										order={i + 1}
										setEditedId={setEditedId}
										key={directionData.id}
										id={directionData.id.toString()}
										data={directionData}
										openModal={() => setModalIsOpen(true)}
										openEditModal={() => setIsEditModalOpen(true)}
										setDeletedId={setDeletedId}
										editable={editable}
									/>
								);
							})
						) : (
							<h6>No directions added yet</h6>
						)}
						{editable && (
							<Button icon={AddIcon} onClick={() => setIsEditModalOpen(true)}>
								Add direction
							</Button>
						)}
					</StyledDirectionTableContainer>
				</>
			)}

			<Modal
				isOpen={modalIsOpen}
				onRequestClose={() => {
					setDeletedId(null);
					setModalIsOpen(false);
				}}
				style={StyledModal}
				appElement={document.getElementById("app")}
			>
				<ConfirmationModal
					handleDelete={handleDelete}
					deletedId={deletedId}
					closeModal={() => {
						setDeletedId(null);
						setModalIsOpen(false);
					}}
				/>
			</Modal>
			<Modal
				isOpen={isEditModalOpen}
				onRequestClose={() => {
					setIsEditModalOpen(false);
					setEditedId(null);
				}}
				style={StyledPointsModal}
				appElement={document.getElementById("app")}
			>
				<ModalHeader
					onClick={() => {
						setIsEditModalOpen(false);
						setEditedId(null);
					}}
				>
					Add direction
				</ModalHeader>
				{!isLoading && !isError && (
					<AddDirection
						directionData={resultDirections}
						editedId={editedId}
						editedDirections={editedDirections}
						setEditedDirections={setEditedDirections}
						updateDirections={updateDirections}
						closeModal={() => {
							setIsEditModalOpen(false);
							setEditedId(null);
						}}
					/>
				)}
			</Modal>
		</>
	);
}

DecisionDirections.propTypes = {
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	newDecision: PropTypes.bool,
};
